/**
 * PartnerApply — вступление в реферальную программу. Шаг Flow (step="partner_apply").
 * Показывается, когда пользователь ещё не партнёр или его заявка отклонена.
 *
 * Пропсы: program — условия программы (ставки, холд, минималка); onBack; onApplied().
 * Выбор модели: «Реферал» (процент с оборота) или «Агент» (своя наценка сверх базового курса).
 * Согласие с офертой — обязательная галочка, её версия фиксируется на бэке.
 * Кнопка: в мессенджере — нативная MainButton, в вебе — FallbackButton.
 */
import { useCallback, useEffect, useState } from "react";
import { useMainButton } from "@/telegram/useMainButton";
import { Screen, Card, FallbackButton } from "@/components/Screen";
import { BackLink } from "@/components/BackLink";
import { ErrorText } from "@/components/ErrorText";
import { Note } from "@/components/Note";
import { friendlyError, partnerApi, type PartnerModel, type PartnerProgram } from "@/lib/api";

type Props = {
  program: PartnerProgram;
  rejectedReason?: string | null;
  onBack: () => void;
  onApplied: () => void;
};

export function PartnerApply({ program, rejectedReason, onBack, onApplied }: Props) {
  const [model, setModel] = useState<PartnerModel>("referral");
  const [accepted, setAccepted] = useState(false);
  const [offer, setOffer] = useState<string>("");
  const [showOffer, setShowOffer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    partnerApi.offer().then((o) => setOffer(o.text)).catch(() => setOffer(""));
  }, []);

  const submit = useCallback(async () => {
    if (!accepted || busy) return;
    setBusy(true);
    setError("");
    try {
      await partnerApi.apply(model);
      onApplied();
    } catch (e) {
      setError(friendlyError(e, "Не удалось отправить заявку."));
    } finally {
      setBusy(false);
    }
  }, [accepted, busy, model, onApplied]);

  useMainButton({ text: "Отправить заявку", onClick: submit, active: accepted, loading: busy });

  return (
    <Screen
      title="Партнёрская программа"
      subtitle="Приводите клиентов и зарабатывайте с их оборота"
      footer={
        <FallbackButton text="Отправить заявку" onClick={submit} disabled={!accepted} loading={busy} />
      }
    >
      <BackLink onClick={onBack} className="mb-4">← Назад</BackLink>

      {rejectedReason && (
        <Note variant="danger" className="mb-4">
          Предыдущая заявка отклонена: {rejectedReason}. Можно подать заново.
        </Note>
      )}

      <div className="space-y-2.5">
        <ModelCard
          selected={model === "referral"}
          onSelect={() => setModel("referral")}
          title="Реферал"
          desc={`Фиксированный процент с оборота приведённых клиентов — ${program.l1_pct}%. Клиент покупает по обычному курсу TTEX.`}
        />
        <ModelCard
          selected={model === "agent"}
          onSelect={() => setModel("agent")}
          title="Агент"
          desc={
            program.max_markup_pct === null
              ? "Вы сами задаёте наценку сверх базового курса и зарабатываете на ней."
              : `Вы сами задаёте наценку сверх базового курса — до +${program.max_markup_pct}%.`
          }
        />
      </div>

      <Card className="mt-4">
        <p className="text-[13px] leading-relaxed text-white/55">
          Вознаграждение начисляется с завершённых сделок ваших клиентов, {program.hold_days} дней
          холда, вывод от {program.min_payout_usd} $. Вы не увидите персональные данные клиентов —
          только обезличенную статистику.
        </p>
      </Card>

      <label className="mt-4 flex items-start gap-3 text-[14px] leading-snug text-white/70">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="mt-0.5 h-5 w-5 shrink-0 accent-brand"
        />
        <span>
          Согласен с{" "}
          <button type="button" className="text-brand underline" onClick={() => setShowOffer((v) => !v)}>
            офертой партнёра
          </button>
        </span>
      </label>

      {showOffer && (
        <Card className="mt-3 max-h-64 overflow-y-auto">
          <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-white/55">
            {offer || "Текст оферты появится здесь после публикации."}
          </p>
        </Card>
      )}

      <ErrorText>{error}</ErrorText>
    </Screen>
  );
}

function ModelCard({
  selected,
  onSelect,
  title,
  desc,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  desc: string;
}) {
  return (
    <button type="button" onClick={onSelect} className="block w-full text-left">
      <div
        className={`rounded-2xl border p-4 transition ${
          selected ? "border-brand/60 bg-brand/10" : "border-white/[0.06] bg-surface-card"
        }`}
      >
        <div className="flex items-center justify-between">
          <span className="text-[16px] font-semibold">{title}</span>
          <span
            className={`h-5 w-5 rounded-full border ${
              selected ? "border-brand bg-brand" : "border-white/25"
            }`}
          />
        </div>
        <p className="mt-1.5 text-[13px] leading-snug text-white/50">{desc}</p>
      </div>
    </button>
  );
}
